import { useContext, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getApiRoute } from "src/services/Config.js";
import axios from "axios";
import { LoadingButton } from "src/components/blocks/LoadingButton";
import { useMutation } from "@tanstack/react-query";
import { useSnackbarContext } from "src/utils/SnackbarContext";
import { customEvent } from "src/utils/gtag.js";
import { getWebcalFormat, handleOpenWebcal } from "src/utils/Utils.js";
import UAParser from "ua-parser-js";

const DownloadSampleButton = ({ 
  calendar = null, 
  sx = {}, 
  children = "DOWNLOAD SAMPLE",
  variant = "outlined",
  ...props 
}) => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const uaParser = new UAParser();
  const parserResults = uaParser.getResult();
  const isIOS = parserResults.os.name === "iOS";

  // Get calendar info from props or location state
  const calendarData = calendar || location.state?.calendar || null;
  const handle = calendarData?.handle;
  const calendarId = calendarData?._id;
  const upgradeType = searchParams.get("upgradeType") || "STANZA_PLUS_CALENDAR";

  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  // Download the sample calendar
  const handleDownloadSample = async () => {
    if (!handle || !calendarId) {
      throw new Error("Missing handle or calendarId");
    }

    const calendarApiUrl = getApiRoute("calendar", "GET_CALENDAR_SAMPLE", {
      handle,
      calendarId,
    });
    const response = await axios.get(calendarApiUrl, {
      responseType: "blob",
    });

    // Create a URL for the blob
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", `${handle}-sample.ics`);

    // Append to the document and trigger the click
    document.body.appendChild(link);
    link.click();

    if (isIOS) {
      // Open the calendar on the calendar app for iOS
      handleOpenWebcal(
        user,
        getWebcalFormat(calendarApiUrl),
        { name: `${handle}-sample.ics` },
        true,
        true
      );
    }

    // Clean up
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const { mutate: downloadSample, isLoading: isDownloading } = useMutation({
    mutationFn: async () => {
      // Track download attempt
      customEvent({
        name: "user_click_download_sample",
        category: "Upgrade",
        label: "Sample Download Attempt",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendarId,
          calendar_handle: handle,
          upgrade_type: upgradeType,
        },
      });

      return handleDownloadSample();
    },
    onSuccess: () => {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Sample calendar downloaded successfully. You can now open it in your calendar app.",
        "success"
      );
    },
    onError: (error) => {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        error.message || "Failed to download sample calendar",
        "error"
      );
    },
  });

  // Don't render if no calendar data available
  if (!handle || !calendarId) {
    return null;
  }

  return (
    <LoadingButton
      size="grown"
      variant={variant}
      onClick={() => downloadSample()}
      loading={isDownloading}
      sx={{
        height: "auto",
        width: "100%",
        py: 1.5,
        borderRadius: 50,
        fontWeight: 500,
        textTransform: "none",
        ...sx
      }}
      {...props}
    >
      {children}
    </LoadingButton>
  );
};

export default DownloadSampleButton; 